import { UIElementType } from '#types/System.types';
import type {
    EditorUIElement,
    EditorUILayout,
    UIArTableColumnOverride,
    UIElement,
    UILayout,
} from '#types/System.types';

const normalizeColumnConfig = (
    columnConfig: UIArTableColumnOverride[] | undefined,
    validTypeIds?: ReadonlySet<string>,
): UIArTableColumnOverride[] | undefined => {
    if (!columnConfig?.length) return undefined;

    const normalized: UIArTableColumnOverride[] = [];
    const seenTypeIds = new Set<string>();

    for (const config of columnConfig) {
        const typeId = config.typeId.trim();
        if (!typeId || seenTypeIds.has(typeId)) continue;
        if (validTypeIds && !validTypeIds.has(typeId)) continue;

        seenTypeIds.add(typeId);
        const labelOverride = config.labelOverride?.trim();
        if (labelOverride) {
            normalized.push({ typeId, labelOverride });
            continue;
        }
        normalized.push({ typeId });
    }

    return normalized.length > 0 ? normalized : undefined;
};

const columnConfigsMatch = (
    left: UIArTableColumnOverride[] | undefined,
    right: UIArTableColumnOverride[] | undefined,
): boolean => {
    if (!left && !right) return true;
    if (!left || !right) return false;
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i += 1) {
        const leftItem = left[i];
        const rightItem = right[i];
        if (leftItem.typeId !== rightItem.typeId) return false;
        if ((leftItem.labelOverride ?? undefined) !== (rightItem.labelOverride ?? undefined)) return false;
    }

    return true;
};

const normalizeElementColumnConfig = <T extends UIElement | EditorUIElement>(element: T): T => {
    const withChildren = ('children' in element && Array.isArray(element.children))
        ? {
            ...element,
            children: element.children.map((child) =>
                normalizeElementColumnConfig(child as UIElement | EditorUIElement)
            ),
        } as T
        : element;

    if (withChildren.uiElementType !== UIElementType.Table) return withChildren;

    const tableElement = withChildren as T & { columnConfig?: UIArTableColumnOverride[] };
    const normalizedColumnConfig = normalizeColumnConfig(tableElement.columnConfig);
    if (columnConfigsMatch(tableElement.columnConfig, normalizedColumnConfig)) return withChildren;

    if (normalizedColumnConfig) {
        return {
            ...withChildren,
            columnConfig: normalizedColumnConfig,
        } as T;
    }

    const { columnConfig: _columnConfig, ...rest } = withChildren as T & {
        columnConfig?: UIArTableColumnOverride[];
    };
    return rest as T;
};

const normalizeLayoutColumnConfig = <T extends UILayout | EditorUILayout>(layout: T): T => {
    return {
        ...layout,
        structure: normalizeElementColumnConfig(layout.structure as UIElement | EditorUIElement),
    } as T;
};

export {
    normalizeColumnConfig,
    columnConfigsMatch,
    normalizeLayoutColumnConfig,
};
